import React, { forwardRef, memo, useImperativeHandle } from 'react';

import { useDisclosure } from '@chakra-ui/hooks';

import { createEmployee } from 'api/employees';
import { useMutation, useQueryClient } from 'react-query';

import { successToast } from 'utils/successToast';

import { washHub } from 'store/slices';

import { RolesEnum } from 'types/user';
import { RefType, AddEmployeeModalProps } from './AddEmployeeModal.props';

import AddEmployeeModalView from './AddEmployeeModal.view';

const Container = forwardRef<RefType, AddEmployeeModalProps>((_, ref) => {
  const queryClient = useQueryClient();

  const currentWashHub = washHub.selectors.useSelectedWashHub();

  const { isOpen, onOpen, onClose } = useDisclosure();

  useImperativeHandle(
    ref,
    () => ({
      onOpen,
    }),
    [onOpen],
  );

  const { mutate, isLoading } = useMutation(createEmployee, {
    onSuccess: () => {
      successToast('Employee Created Successfully');

      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey?.[0] === `fetch-employees-${currentWashHub?._id}`,
      });

      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey?.[0] === 'fetch-super-admins',
      });

      onClose();
    },
  });

  const _props = {
    isOpen,
    onClose,
    isLoading,
    onCreate: async (values: any) => {
      const isWashHubMember = [
        RolesEnum.SUPER_ADMIN,
        RolesEnum.WASH_HUB_ADMIN,
        RolesEnum.WASH_HUB_STAFF,
      ].includes(values?.role);

      const washHub = isWashHubMember ? currentWashHub?._id : undefined;
      mutate({ ...values, washHub });
    },
  };

  return <AddEmployeeModalView {..._props} />;
});

const AddEmployeeModal = memo(Container);

export default AddEmployeeModal;
